import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html, type PropertyValues } from 'lit'
import { property, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { catchError, map, of, ReplaySubject, startWith, switchMap, takeUntil } from 'rxjs'
import { xmlDocument, type XmlDocument } from './xml'
import '../typography/typography'

type XmlThumbnailResult = { status: 'loading' | 'error' } | { status: 'ready'; document: XmlDocument }

@schmancyElement('schmancy-xml-thumbnail')
export class SchmancyXmlThumbnail extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				height: 100%;
				min-width: 0;
				overflow: hidden;
				pointer-events: none;
			}
		`,
	]
	@property() src = ''
	@property() label = ''
	@property({ attribute: 'failed-label' }) failedLabel?: string
	@state() private result: XmlThumbnailResult = { status: 'loading' }
	private sources = new ReplaySubject<string>(1)

	override connectedCallback() {
		super.connectedCallback()
		this.sources
			.pipe(
				switchMap(src =>
					src
						? xmlDocument(src).pipe(
								map((document): XmlThumbnailResult => ({ status: 'ready', document })),
								catchError(error => {
									this.dispatchEvent(new CustomEvent('load-failed', { detail: error, bubbles: true, composed: true }))
									return of<XmlThumbnailResult>({ status: 'error' })
								}),
								startWith<XmlThumbnailResult>({ status: 'loading' }),
							)
						: of<XmlThumbnailResult>({ status: 'loading' }),
				),
				takeUntil(this.disconnecting),
			)
			.subscribe(result => {
				this.result = result
				if (result.status === 'ready') this.dispatchEvent(new Event('load', { bubbles: true, composed: true }))
			})
	}

	override willUpdate(changed: PropertyValues<this>) {
		if (changed.has('src')) this.sources.next(this.src)
	}

	override render() {
		if (this.result.status !== 'ready')
			return html`<div class="grid place-content-center h-full p-2 text-center" role="status">
				<schmancy-typography type="label" token="sm"
					>${this.result.status === 'loading' ? componentText('xml.loading') : this.failedLabel || componentText('xml.failed')}</schmancy-typography
				>
			</div>`
		const document = this.result.document
		return html`<svg
			class="block w-full h-full"
			viewBox="0 0 400 360"
			preserveAspectRatio="xMinYMin meet"
			role="img"
			aria-label=${componentText('xml.thumbnail', { name: this.label || componentText('xml.invoice') })}
		>
			<foreignObject width="400" height="360"
				><div
					class="grid content-start gap-5 p-6 h-90 overflow-hidden box-border bg-surface-containerLowest text-surface-on"
					aria-hidden="true"
				>
					${
						document.kind === 'invoice'
							? html`
									<div class="grid gap-1 content-start min-w-0">
										<schmancy-typography type="label" token="sm" class="text-surface-onVariant"
											>${document.invoice.syntax} XML</schmancy-typography
										><schmancy-typography type="title" token="lg" class="block truncate"
											>${document.invoice.number || componentText('xml.invoice')}</schmancy-typography
										><schmancy-typography type="body" token="sm"
											>${document.invoice.issueDate || ''}</schmancy-typography
										>
									</div>
									<div class="grid grid-cols-2 gap-4">
										${repeat(
											[document.invoice.seller, document.invoice.buyer],
											(_item, index) => index,
											party =>
												html`<div class="grid gap-1 content-start min-w-0">
													<schmancy-typography type="label" token="sm" class="block truncate"
														>${party.name || ''}</schmancy-typography
													>${repeat(
														party.address.slice(0, 2),
														(_item, index) => index,
														line =>
															html`<schmancy-typography
																type="body"
																token="sm"
																class="block truncate text-surface-onVariant"
																>${line}</schmancy-typography
															>`,
													)}
												</div>`,
										)}
									</div>
									<div class="grid gap-2">
										${repeat(
											document.invoice.lines.slice(0, 3),
											(_item, index) => index,
											line =>
												html`<div class="flex justify-between gap-4 min-w-0">
													<schmancy-typography type="body" token="sm" class="block truncate"
														>${line.name || line.description || line.id || ''}</schmancy-typography
													><schmancy-typography type="body" token="sm">${line.net || ''}</schmancy-typography>
												</div>`,
										)}
									</div>
									${when(document.invoice.totals.due, () => html`<div class="flex items-baseline justify-between gap-4 p-3 rounded-lg bg-surface-container tabular-nums"><schmancy-typography type="label" token="sm">${componentText('xml.amountDue')}</schmancy-typography><schmancy-typography type="title" token="lg">${document.invoice.totals.due} ${document.invoice.currency || ''}</schmancy-typography></div>`)}
								`
							: html`<div class="grid gap-1 content-start min-w-0">
										<schmancy-typography type="title" token="lg">${document.rootName}</schmancy-typography
										><schmancy-typography type="label" token="sm" class="text-surface-onVariant"
											>XML</schmancy-typography
										>
									</div>
									<pre class="m-0 font-mono text-xs leading-relaxed whitespace-pre-wrap wrap-anywhere overflow-hidden">
${document.source.slice(0, 1800)}</pre>`
					}
				</div></foreignObject
			>
		</svg>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-xml-thumbnail': SchmancyXmlThumbnail
	}
}
